/**
 * ProjectsPanel — Fund Discovery Projects Manager (v3 architecture)
 * Shows org projects, allows creating/selecting them, and contextualizes FundMatch.
 * Includes full project settings (edit modal) with funding range, location,
 * target population, and all other project data fields.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderOpen, Plus, ChevronRight, Check, Loader2,
  DollarSign, Calendar, Tag, X, Briefcase, Settings2,
  MapPin, Users,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  useOrgProjects,
  useCreateProject,
  useUpdateProject,
} from "../hooks/useProjects";

const PROJECT_TYPE_COLORS = {
  program:   "bg-teal-100 text-teal-700",
  capital:   "bg-blue-100 text-blue-700",
  operating: "bg-violet-100 text-violet-700",
  research:  "bg-amber-100 text-amber-700",
  endowment: "bg-rose-100 text-rose-700",
  other:     "bg-zinc-100 text-zinc-600",
};

const STATUS_COLORS = {
  draft:    "bg-zinc-100 text-zinc-600",
  active:   "bg-green-100 text-green-700",
  closed:   "bg-red-100 text-red-700",
  archived: "bg-gray-100 text-gray-500",
};

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const PROJECT_TYPES = ["program", "capital", "operating", "research", "endowment", "other"];
const PROJECT_STATUSES = ["draft", "active", "closed", "archived"];
const currentYear = new Date().getFullYear();

const EMPTY_FORM = {
  project_name: "",
  project_type: "program",
  cycle_year: currentYear,
  budget_ask: "",
  budget_min: "",
  target_deadline: "",
  target_population: "",
  geographic_focus: "",
  project_description: "",
  l2_focus_area: "",
  l3_methods: "",
  l4_tools: "",
};

// ── Shared form fields (used by both create and edit) ────────────────────────
function ProjectFormFields({ form, set, showGoals, setShowGoals, isEdit = false }) {
  return (
    <div className="space-y-4">
      {/* Row 1 — Name + Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-zinc-700 block mb-1">
            Project Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.project_name}
            onChange={(e) => set("project_name", e.target.value)}
            placeholder="Youth Mentorship Program 2026"
            required
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-700 block mb-1">
            Project Type
          </label>
          <select
            value={form.project_type}
            onChange={(e) => set("project_type", e.target.value)}
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2 — Funding Range */}
      <div>
        <label className="text-xs font-medium text-zinc-700 block mb-1">
          Funding Range ($)
          <span className="text-zinc-400 font-normal ml-1">— used to filter grants by grant size</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input
              type="number"
              value={form.budget_min}
              onChange={(e) => set("budget_min", e.target.value)}
              placeholder="Min acceptable (e.g. 25000)"
              min="0"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-zinc-400 mt-0.5">Minimum acceptable</p>
          </div>
          <div>
            <input
              type="number"
              value={form.budget_ask}
              onChange={(e) => set("budget_ask", e.target.value)}
              placeholder="Target ask (e.g. 75000)"
              min="0"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-zinc-400 mt-0.5">Target / preferred ask</p>
          </div>
        </div>
      </div>

      {/* Row 3 — Location of Deployment */}
      <div>
        <label className="text-xs font-medium text-zinc-700 block mb-1">
          Location of Deployment
        </label>
        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.geographic_focus}
            onChange={(e) => set("geographic_focus", e.target.value)}
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Select province / territory…</option>
            <option value="national">🇨🇦 National (all Canada)</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="text"
            value={form.target_population}
            onChange={(e) => set("target_population", e.target.value)}
            placeholder="e.g. At-risk youth, Seniors, Indigenous communities"
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex gap-4 mt-0.5">
          <p className="text-xs text-zinc-400 flex-1">Province / territory</p>
          <p className="text-xs text-zinc-400 flex-1">Target population</p>
        </div>
      </div>

      {/* Row 4 — Cycle Year + Deadline + Status (edit only) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-zinc-700 block mb-1">Cycle Year</label>
          <input
            type="number"
            value={form.cycle_year}
            onChange={(e) => set("cycle_year", e.target.value)}
            min="2020" max="2030"
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-700 block mb-1">
            Application Deadline
          </label>
          <input
            type="date"
            value={form.target_deadline}
            onChange={(e) => set("target_deadline", e.target.value)}
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        {isEdit && (
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">Status</label>
            <select
              value={form.status || "draft"}
              onChange={(e) => set("status", e.target.value)}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Description / Project Goal */}
      <div>
        <label className="text-xs font-medium text-zinc-700 block mb-1">
          Project Goal & Description
        </label>
        <textarea
          value={form.project_description}
          onChange={(e) => set("project_description", e.target.value)}
          rows={3}
          placeholder="Describe what this project will do, who it serves, and its expected outcomes. This text is used for semantic grant matching."
          className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
        />
      </div>

      {/* L2-L4 Goals (collapsible) */}
      <div>
        <button
          type="button"
          onClick={() => setShowGoals(!showGoals)}
          className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium"
        >
          <ChevronRight
            className={`h-3 w-3 transition-transform ${showGoals ? "rotate-90" : ""}`}
          />
          L2-L4 Taxonomy Goals (optional — AI will extract from description if omitted)
        </button>
        {showGoals && (
          <div className="mt-3 space-y-2 pl-4 border-l-2 border-teal-100">
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">L2 Focus Area</label>
              <input
                type="text"
                value={form.l2_focus_area}
                onChange={(e) => set("l2_focus_area", e.target.value)}
                placeholder="e.g. Youth Employment, Housing Security"
                className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">L3 Methods</label>
              <input
                type="text"
                value={form.l3_methods}
                onChange={(e) => set("l3_methods", e.target.value)}
                placeholder="e.g. Mentorship, Job Training, Case Management"
                className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">L4 Tools / Resources</label>
              <input
                type="text"
                value={form.l4_tools}
                onChange={(e) => set("l4_tools", e.target.value)}
                placeholder="e.g. Digital Curriculum, Employer Network"
                className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create form (inline card) ─────────────────────────────────────────────────
function NewProjectForm({ bn, onClose, onCreate }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showGoals, setShowGoals] = useState(false);
  const createMutation = useCreateProject(bn);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_name.trim()) return;
    const payload = {
      ...form,
      budget_ask: form.budget_ask ? parseFloat(form.budget_ask) : null,
      budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
      cycle_year: parseInt(form.cycle_year) || currentYear,
      target_deadline: form.target_deadline || null,
    };
    try {
      const created = await createMutation.mutateAsync(payload);
      onCreate(created);
      onClose();
    } catch (err) {
      console.error("Failed to create project", err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card className="border-teal-200 shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-900">New Project</CardTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription className="text-xs">
            Each project runs its own grant discovery and tracks its own pipeline.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ProjectFormFields
              form={form}
              set={set}
              showGoals={showGoals}
              setShowGoals={setShowGoals}
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                size="sm"
                disabled={createMutation.isPending || !form.project_name.trim()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {createMutation.isPending ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Creating…
                  </span>
                ) : "Create Project"}
              </Button>
            </div>
            {createMutation.isError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700 text-xs">
                  {createMutation.error?.response?.data?.detail || "Failed to create project. Try again."}
                </AlertDescription>
              </Alert>
            )}
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ── Edit project settings modal ───────────────────────────────────────────────
function EditProjectModal({ project, bn, onClose }) {
  const [showGoals, setShowGoals] = useState(!!(project.goals?.l2_focus_area));
  const [saveError, setSaveError] = useState(null);
  const updateMutation = useUpdateProject(bn);

  const [form, setFormState] = useState({
    project_name:        project.project_name || "",
    project_type:        project.project_type || "program",
    cycle_year:          project.cycle_year || currentYear,
    budget_ask:          project.budget_ask != null ? String(project.budget_ask) : "",
    budget_min:          project.budget_min != null ? String(project.budget_min) : "",
    target_deadline:     project.target_deadline ? project.target_deadline.slice(0, 10) : "",
    target_population:   project.target_population || "",
    geographic_focus:    project.geographic_focus || "",
    project_description: project.project_description || "",
    l2_focus_area:       project.goals?.l2_focus_area || "",
    l3_methods:          project.goals?.l3_methods || "",
    l4_tools:            project.goals?.l4_tools || "",
    status:              project.status || "draft",
  });

  const set = (key, val) => setFormState((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaveError(null);
    const payload = {
      project_name:        form.project_name.trim(),
      project_type:        form.project_type,
      cycle_year:          parseInt(form.cycle_year) || currentYear,
      budget_ask:          form.budget_ask ? parseFloat(form.budget_ask) : null,
      budget_min:          form.budget_min ? parseFloat(form.budget_min) : null,
      target_deadline:     form.target_deadline || null,
      target_population:   form.target_population || null,
      geographic_focus:    form.geographic_focus || null,
      project_description: form.project_description || null,
      l2_focus_area:       form.l2_focus_area || null,
      l3_methods:          form.l3_methods || null,
      l4_tools:            form.l4_tools || null,
      status:              form.status,
    };
    try {
      await updateMutation.mutateAsync({ projectId: project.project_id, updates: payload });
      onClose();
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Failed to save project settings.");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-teal-600" />
            Project Settings
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Edit all data for <strong>{project.project_name}</strong>.
            Funding range, location, and goals are used in grant matching.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <ProjectFormFields
            form={form}
            set={set}
            showGoals={showGoals}
            setShowGoals={setShowGoals}
            isEdit
          />
        </div>

        {saveError && (
          <Alert className="border-red-200 bg-red-50 mt-3">
            <AlertDescription className="text-red-700 text-xs">{saveError}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-100 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending || !form.project_name.trim()}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {updateMutation.isPending ? (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </span>
            ) : "Save Settings"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Project card ──────────────────────────────────────────────────────────────
function ProjectCard({ project, isSelected, onSelect, onEdit }) {
  const typeClass   = PROJECT_TYPE_COLORS[project.project_type] || PROJECT_TYPE_COLORS.other;
  const statusClass = STATUS_COLORS[project.status] || STATUS_COLORS.draft;

  const budgetMin = project.budget_min;
  const budgetAsk = project.budget_ask;

  const fmtAmt = (n) => n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : `$${(n / 1_000).toFixed(0)}K`;

  return (
    <motion.div
      layout
      className={`
        relative rounded-lg border p-2 transition-all min-w-[190px] max-w-[240px]
        ${isSelected
          ? "border-teal-400 bg-teal-50 shadow-md"
          : "border-zinc-200 bg-white hover:border-teal-200 hover:shadow-sm"
        }
      `}
    >
      {/* Settings button */}
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(project); }}
        className="absolute top-1.5 right-1.5 p-1 rounded-md text-zinc-300 hover:text-teal-600 hover:bg-teal-50 transition-colors"
        title="Project settings"
      >
        <Settings2 className="h-3 w-3" />
      </button>

      {/* Select area */}
      <div
        className="cursor-pointer"
        onClick={() => onSelect(isSelected ? null : project)}
      >
        {isSelected && (
          <div className="absolute top-1.5 left-1.5 w-3.5 h-3.5 rounded-full bg-teal-500 flex items-center justify-center">
            <Check className="h-2 w-2 text-white" />
          </div>
        )}

        <div className="mb-1 pl-1">
          <div className={`flex items-start gap-1 ${isSelected ? "pl-3" : ""} pr-5`}>
            <Briefcase className="h-3 w-3 text-zinc-400 mt-0.5 shrink-0" />
            <p className="text-[12px] font-semibold text-zinc-900 leading-tight line-clamp-1">
              {project.project_name}
            </p>
          </div>
          <p className="text-[9px] text-zinc-500 ml-4 mt-0">Cycle {project.cycle_year}</p>
        </div>

        <div className="flex flex-wrap gap-0.5 mb-1">
          <Badge className={`text-[9px] px-1 py-0 h-3.5 ${typeClass}`}>{project.project_type}</Badge>
          <Badge className={`text-[9px] px-1 py-0 h-3.5 ${statusClass}`}>{project.status}</Badge>
        </div>

        {/* Funding range */}
        {(budgetMin || budgetAsk) && (
          <div className="flex items-center gap-1 text-[10px] text-zinc-600 mb-0">
            <DollarSign className="h-2 w-2" />
            <span>
              {budgetMin && budgetAsk
                ? `${fmtAmt(budgetMin)} – ${fmtAmt(budgetAsk)}`
                : budgetAsk
                ? `${fmtAmt(budgetAsk)} ask`
                : `Min ${fmtAmt(budgetMin)}`}
            </span>
          </div>
        )}

        {/* Location */}
        {project.geographic_focus && (
          <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0">
            <MapPin className="h-2 w-2" />
            <span>{project.geographic_focus === "national" ? "🇨🇦 National" : project.geographic_focus}</span>
          </div>
        )}

        {/* Population */}
        {project.target_population && (
          <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0">
            <Users className="h-2 w-2" />
            <span className="truncate">{project.target_population}</span>
          </div>
        )}

        {project.target_deadline && (
          <div className="flex items-center gap-1 text-[10px] text-zinc-500">
            <Calendar className="h-2 w-2" />
            <span>
              {new Date(project.target_deadline).toLocaleDateString("en-CA", {
                month: "short", day: "numeric",
              })}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export function ProjectsPanel({ charityBN, selectedProject, onProjectSelect }) {
  const [showForm, setShowForm]       = useState(false);
  const [editProject, setEditProject] = useState(null);
  const { data, isLoading, error }    = useOrgProjects(charityBN);
  const projects = data?.projects || [];

  // Auto-select a project ONLY if there's already a match run for it (to avoid flickering grants)
  // If user has no projects with prior matches, stay on org-level view
  const projectsWithRuns = projects.filter(p => p.has_match_run);
  const projectToAutoSelect = projectsWithRuns.length > 0 ? projectsWithRuns[0] : null;

  useEffect(() => {
    // Only auto-select if there's a project with a prior match run
    // This prevents flickering from org-level grants to empty project view
    if (!selectedProject && projectToAutoSelect) {
      onProjectSelect(projectToAutoSelect);
    }
  }, [projectToAutoSelect, selectedProject, onProjectSelect]);

  if (!charityBN) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-zinc-400 text-sm">
          Complete onboarding to create and manage your funding projects.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="py-1.5 px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-teal-600" />
              <div>
                <CardTitle className="text-sm">My Projects</CardTitle>
                <CardDescription className="text-[11px]">
                  Each project runs its own grant discovery, pipeline and compliance tracking.
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setShowForm(!showForm)}
              className="bg-teal-600 hover:bg-teal-700 text-white h-7 text-xs px-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              New Project
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-3 pt-0 space-y-2">
          {/* New project form */}
          <AnimatePresence>
            {showForm && (
              <NewProjectForm
                bn={charityBN}
                onClose={() => setShowForm(false)}
                onCreate={(p) => {
                  onProjectSelect(p);
                  setShowForm(false);
                }}
              />
            )}
          </AnimatePresence>

          {/* Project list */}
          {isLoading ? (
            <div className="flex items-center gap-2 text-zinc-400 text-sm py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading projects…
            </div>
          ) : error ? (
            <p className="text-sm text-red-500">Could not load projects.</p>
          ) : projects.length === 0 ? (
            <div className="py-6 text-center">
              <FolderOpen className="h-10 w-10 text-zinc-200 mx-auto mb-2" />
              <p className="text-sm text-zinc-500 mb-1">No projects yet</p>
              <p className="text-xs text-zinc-400">
                Create your first project to run targeted grant discovery,
                track applications, and measure year-over-year progress.
              </p>
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {projects.map((project) => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  isSelected={selectedProject?.project_id === project.project_id}
                  onSelect={onProjectSelect}
                  onEdit={setEditProject}
                />
              ))}
            </div>
          )}

          {/* Selected project context banner */}
          {selectedProject && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg px-4 py-2"
            >
              <div className="flex items-center gap-2 text-sm text-teal-800">
                <Check className="h-4 w-4 text-teal-500" />
                <span>
                  Grant matches contextualized for{" "}
                  <strong>{selectedProject.project_name}</strong>
                  {selectedProject.budget_ask && (
                    <> — ${(selectedProject.budget_ask / 1000).toFixed(0)}K ask</>
                  )}
                  {selectedProject.geographic_focus && (
                    <> · {selectedProject.geographic_focus === "national" ? "🇨🇦 Canada" : selectedProject.geographic_focus}</>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-teal-600 hover:text-teal-800 h-6 px-2 text-xs"
                  onClick={() => setEditProject(selectedProject)}
                >
                  <Settings2 className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-zinc-700 h-6 px-2 text-xs"
                  onClick={() => onProjectSelect(null)}
                >
                  Clear
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Edit modal */}
      {editProject && (
        <EditProjectModal
          project={editProject}
          bn={charityBN}
          onClose={() => setEditProject(null)}
        />
      )}
    </>
  );
}

export default ProjectsPanel;
